<template>
  <div class="form-responses-table">
    <b-row class="mb-2">
      <b-col cols="12">
        <div class="float-right d-flex">
          <b-input-group class="search-group d-flex align-items-center">
            <b-icon
                icon="search"
                aria-hidden="true"
                scale="1"
            ></b-icon>
            <b-form-input
                id="response-filter-input"
                v-model="filter"
                type="search"
                :placeholder="$t('webmag.search')"
            ></b-form-input>
          </b-input-group>
        </div>
      </b-col>
    </b-row>
    <b-table
        id="formResponsesTable"
        :fields="fields"
        :items="tableItems"
        :filter="filter"
        sort-icon-left
        show-empty
        borderless
        dark
        :empty-text="$t('settings.forms.noResponses')"
        :empty-filtered-text="$t('settings.forms.noResponses')"
        class="table-view responses-table"
    >
      <template #table-colgroup="scope">
        <col
            v-for="field in scope.fields"
            :key="field.key"
            :style="{ width: field.width }"
        >
      </template>

      <template #cell(toggle)="data">
        <div class="chevron-parent">
          <div class="contacts-header-arrows d-flex" @click="data.toggleDetails">
            <b-icon
                :icon="data.item._showDetails ? 'chevron-down' : 'chevron-right'"
                class="top-4 position-relative"
                style="color: #fff"
                aria-hidden="true"
                scale="1.2"
            ></b-icon>
          </div>
        </div>
      </template>

      <template #cell(createdAt)="data">
        <div @click="data.toggleDetails">
          {{ formatDate(data.item.createdAt) }}
        </div>
      </template>

      <template #cell(language)="data">
        <div @click="data.toggleDetails">
          {{ data.item.language || '–' }}
        </div>
      </template>

      <template #cell(actions)="data">
        <b-button
            variant="link"
            class="delete-response-button"
            :disabled="data.item.id < 0"
            :title="$t('settings.forms.deleteResponse')"
            @click="confirmDelete(data.item)"
        >
          <b-icon icon="trash" aria-hidden="true" scale="1"></b-icon>
        </b-button>
      </template>

      <template #row-details="data">
        <div class="response-detail">
          <template v-if="allBlocks.length">
            <div
                v-if="answeredBlocks(data.item).length"
                class="detail-grid"
            >
              <div
                  v-for="(block, index) in answeredBlocks(data.item)"
                  :key="`a-${block.id}-${index}`"
                  class="detail-row"
              >
                <div class="detail-question">{{ blockTitle(block) }}</div>
                <div class="detail-answer">{{ answerText(block, data.item) }}</div>
              </div>
            </div>
            <template v-if="unansweredBlocks(data.item).length">
              <template v-if="answeredBlocks(data.item).length">
                <button
                    type="button"
                    class="unanswered-toggle"
                    @click="toggleUnanswered(data.item.id)"
                >
                  <b-icon
                      :icon="isUnansweredVisible(data.item.id)
                        ? 'chevron-up' : 'chevron-down'"
                      class="mr-1"
                      aria-hidden="true"
                      scale="0.8"
                  ></b-icon>
                  {{ unansweredToggleLabel(data.item) }}
                </button>
                <div
                    v-if="isUnansweredVisible(data.item.id)"
                    class="detail-grid detail-grid--unanswered"
                >
                  <div
                      v-for="(block, index) in unansweredBlocks(data.item)"
                      :key="`u-${block.id}-${index}`"
                      class="detail-row"
                  >
                    <div class="detail-question">{{ blockTitle(block) }}</div>
                    <div class="detail-answer">–</div>
                  </div>
                </div>
              </template>
              <div v-else class="detail-grid detail-grid--unanswered">
                <div
                    v-for="(block, index) in unansweredBlocks(data.item)"
                    :key="`u-${block.id}-${index}`"
                    class="detail-row"
                >
                  <div class="detail-question">{{ blockTitle(block) }}</div>
                  <div class="detail-answer">–</div>
                </div>
              </div>
            </template>
          </template>
          <pre v-else class="response-details-raw mb-0">{{ rawValues(data.item) }}</pre>
          <div v-if="variables.length" class="detail-grid detail-grid--variables">
            <div class="detail-section-title">Berechnet</div>
            <div
                v-for="row in variableRows(data.item)"
                :key="`v-${row.id}`"
                class="detail-row"
            >
              <div class="detail-question">{{ row.name }}</div>
              <div class="detail-answer">{{ row.value }}</div>
            </div>
          </div>
          <div class="detail-meta">
            <span>{{ formatDateLong(data.item.createdAt) }}</span>
            <span v-if="data.item.language">{{ data.item.language }}</span>
            <span v-if="metaPageUrl(data.item)" class="detail-meta-url">
              {{ metaPageUrl(data.item) }}
            </span>
          </div>
        </div>
      </template>
    </b-table>
  </div>
</template>

<script>
import {
  inputBlocksOf,
  answerFor,
  localized,
  variablesOf,
  variableValueFor,
} from '@/components/settings/forms/formResponses.helpers';

export default {
  name: 'FormResponsesTable',
  props: {
    formDoc: {
      type: Object,
      default: null,
    },
    responses: {
      type: Array,
      default: () => [],
    },
    canDelete: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filter: null,
      unansweredVisible: {},
    };
  },
  computed: {
    defaultLanguage() {
      return (this.formDoc && this.formDoc.defaultLanguage) || 'de-DE';
    },
    allBlocks() {
      return this.formDoc ? inputBlocksOf(this.formDoc) : [];
    },
    variables() {
      return this.formDoc ? variablesOf(this.formDoc) : [];
    },
    // Four preview columns, as before. A calculator's scores say more at a
    // glance than its first questions, so variables take the columns first.
    previewVariables() {
      return this.variables.slice(0, 4);
    },
    previewBlocks() {
      return this.allBlocks.slice(0, 4 - this.previewVariables.length);
    },
    fields() {
      const fields = [
        {
          key: 'toggle',
          label: '',
          sortable: false,
          width: '50px',
        },
      ];
      this.previewVariables.forEach((variable) => {
        fields.push({
          key: `v_${variable.id}`,
          label: this.truncate(variable.name || variable.id, 24),
          sortable: false,
          width: 'auto',
        });
      });
      this.previewBlocks.forEach((block) => {
        fields.push({
          key: `q_${block.id}`,
          label: this.truncate(this.blockTitle(block), 24),
          sortable: false,
          width: 'auto',
        });
      });
      fields.push({
        key: 'createdAt',
        label: this.$t('settings.forms.date'),
        sortable: true,
        width: '120px',
      });
      fields.push({
        key: 'language',
        label: this.$t('settings.forms.languageCol'),
        sortable: true,
        width: '100px',
      });
      if (this.canDelete) {
        fields.push({
          key: 'actions',
          label: '',
          sortable: false,
          width: '50px',
        });
      }
      return fields;
    },
    tableItems() {
      return (this.responses || []).map((response) => {
        const row = {
          id: response.id,
          createdAt: response.createdAt,
          language: response.language,
          meta: response.meta || null,
          values: response.values || {},
          _showDetails: false,
        };
        this.previewBlocks.forEach((block) => {
          row[`q_${block.id}`] = answerFor(block, row.values, this.answerLanguage(response));
        });
        this.previewVariables.forEach((variable) => {
          row[`v_${variable.id}`] = variableValueFor(variable, row.meta);
        });
        const variableText = this.variables
          .map((variable) => variableValueFor(variable, row.meta))
          .join(' ');
        row.searchText = `${this.allBlocks.length
          ? this.allBlocks
            .map((block) => answerFor(block, row.values, this.answerLanguage(response)))
            .join(' ')
          : JSON.stringify(row.values)} ${variableText}`.trim();
        return row;
      });
    },
  },
  methods: {
    answerLanguage(response) {
      return (response && response.language) || this.defaultLanguage;
    },
    blockTitle(block) {
      return localized(block.label, this.defaultLanguage, this.defaultLanguage);
    },
    truncate(value, length) {
      const str = String(value || '');
      return (str.length > length) ? `${str.slice(0, length)}...` : str;
    },
    formatDate(value) {
      if (!value) return '–';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value).substring(0, 10);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    },
    answerText(block, item) {
      return answerFor(block, item.values || {}, item.language || this.defaultLanguage);
    },
    isAnswered(block, item) {
      return this.answerText(block, item) !== '–';
    },
    answeredBlocks(item) {
      return this.allBlocks.filter((block) => this.isAnswered(block, item));
    },
    unansweredBlocks(item) {
      return this.allBlocks.filter((block) => !this.isAnswered(block, item));
    },
    toggleUnanswered(id) {
      this.$set(this.unansweredVisible, id, !this.unansweredVisible[id]);
    },
    isUnansweredVisible(id) {
      return !!this.unansweredVisible[id];
    },
    unansweredToggleLabel(item) {
      const count = this.unansweredBlocks(item).length;
      const noun = count === 1 ? 'unbeantwortete Frage' : 'unbeantwortete Fragen';
      const verb = this.isUnansweredVisible(item.id) ? 'ausblenden' : 'anzeigen';
      return `${count} ${noun} ${verb}`;
    },
    formatDateLong(value) {
      if (!value) return '–';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleDateString('de-DE', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    metaPageUrl(item) {
      const { meta } = item;
      if (!meta || typeof meta !== 'object') return null;
      return meta.pageUrl || meta.page_url || meta.url || null;
    },
    variableRows(item) {
      return this.variables.map((variable) => ({
        id: variable.id,
        name: variable.name || variable.id,
        value: variableValueFor(variable, item.meta),
      }));
    },
    rawValues(item) {
      return JSON.stringify(item.values || {}, null, 2);
    },
    confirmDelete(item) {
      const h = this.$createElement;
      const messageVNode = h('div', { class: ['text-center', 'p-4'] }, [
        h('b-icon', {
          props: { icon: 'exclamation-triangle-fill', variant: 'danger' },
          class: ['mt-3', 'mb-3'],
          style: { fontSize: '2rem' },
        }),
        h('div', { class: ['mt-2'] }, this.$t('settings.forms.deleteResponseConfirm')),
      ]);
      this.$bvModal.msgBoxConfirm([messageVNode], {
        title: this.$t('settings.forms.deleteResponse'),
        okTitle: this.$t('settings.forms.deleteResponse'),
        okVariant: 'danger',
        cancelTitle: this.$t('buttons.cancel'),
        footerClass: 'flex-nowrap',
        centered: true,
        noCloseOnBackdrop: true,
      })
        .then((value) => {
          if (value) {
            this.$emit('delete-response', item.id);
          }
        })
        .catch(() => {
          // modal dismissed programmatically — nothing to do
        });
    },
  },
};
</script>

<style scoped lang="scss">
.table-dark {
  background-color: transparent;
}

::v-deep table.table-view {
  font-size: 15px;
  border-collapse: collapse;
  border-spacing: 0 8px;
  color: $webmag-light-grey;

  thead th {
    text-transform: uppercase;
    font-size: 13px;
    padding: 7px 8px;
  }

  tbody tr:not([class="b-table-has-details"]) {
    border-bottom: 8px solid #44484c;
  }

  tbody tr {
    background-color: #53575b;
    box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);

    > td {
      padding: 7px 8px;
      vertical-align: middle;

      &:first-child {
        padding: 0;
        vertical-align: middle;
        text-align: center;
      }
    }
  }

  tr.b-table-details {
    background-color: transparent;
    box-shadow: none;

    > td {
      padding: 0 0 8px 0;
    }
  }
}

::v-deep .delete-response-button {
  padding: 0;
  line-height: 1;
  color: $webmag-light-grey;

  &:hover:not(:disabled) {
    color: $webmag-red-light;
  }

  &:disabled {
    opacity: 0.3;
  }
}

.chevron-parent {
  flex-basis: 40px;

  .contacts-header-arrows {
    background-color: $webmag-grey;
    width: 50px;
    align-items: center;
    justify-content: center;
    height: 2.5em;

    svg {
      cursor: pointer;
    }
  }
}

.response-detail {
  background-color: #4b4f53;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);
  padding: 16px 20px;
  text-align: left;
}

.detail-grid {
  .detail-row {
    display: grid;
    grid-template-columns: minmax(160px, 40%) 1fr;
    column-gap: 24px;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);

    &:last-child {
      border-bottom: none;
    }
  }

  .detail-question {
    font-weight: 600;
    color: $webmag-light-grey;
    overflow-wrap: break-word;
  }

  .detail-answer {
    color: $webmag-lighter-grey;
    overflow-wrap: break-word;
    word-break: break-word;
    white-space: pre-line;
    min-width: 0;
  }

  &--unanswered {
    .detail-question,
    .detail-answer {
      color: $webmag-grey;
      font-weight: 400;
    }
  }
}

.detail-section-title {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  opacity: 0.7;
  margin-top: 12px;
}

.unanswered-toggle {
  display: block;
  background: transparent;
  border: none;
  padding: 10px 0 2px 0;
  font-size: 13px;
  color: $webmag-light-grey;

  &:hover,
  &:focus {
    color: #fff;
    outline: none;
  }
}

.detail-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 20px;
  margin-top: 14px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 13px;
  color: $webmag-grey;

  .detail-meta-url {
    overflow-wrap: anywhere;
  }
}

.response-details-raw {
  font-size: 13px;
  white-space: pre-wrap;
  color: $webmag-lighter-grey;
}
</style>
